/**
 * /app/$tenantSlug/me/documents — Me → Documents: my own document store
 * (contracts, ID proofs, certificates, anything HR shared with me), with
 * Upload. The Letters tab (/me/letters) holds the letters HR sent me.
 *
 * Navigation rebuild (wave 3, 28 Oct 2026; employee review finding #1):
 * employees used to find their documents at /hr/documents, inside the HR
 * module's tab strip, whose other tabs were HR-only dead ends. This page
 * shows the same store with no HR tabs and no admin controls, for every
 * role (an HR admin sees their OWN documents here; the company store with
 * the employee picker stays at /hr/documents). A non-HR visitor to
 * /hr/documents is redirected here (lib/me-redirects.ts).
 *
 * GET /hr/documents?user_id=<me> is self-scoped server-side and filtered by
 * document visibility (models.CanViewDocument); nothing is filtered here.
 * hr_core-gated like the HR page (HrFeatureGate).
 */

import { createFileRoute } from "@tanstack/react-router";
import { useAuth, useFeatures } from "@/lib/auth";
import { HrFeatureGate } from "@/components/hr/hr-feature-gate";
import { DocumentList } from "@/components/hr/document-list";
import { DocumentUploadDialog } from "@/components/hr/document-upload-dialog";
import { MeModuleShell } from "@/components/shared/me-module-shell";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/me/documents")({
  component: MyDocumentsRoute,
});

function MyDocumentsRoute() {
  const { user } = useAuth();
  const { has } = useFeatures();
  const selfId = user?.user_id;
  // Upload sits outside HrFeatureGate (in the shell's actions), so it
  // checks the plan itself: without hr_core the server refuses it.
  const canUpload = selfId != null && has("hr_core");

  return (
    <MeModuleShell
      title="Documents"
      description="Your contracts, ID proofs, certificates and other files HR keeps for you."
      actions={
        canUpload ? (
          <DocumentUploadDialog subjectUserId={selfId} subjectLabel="your" />
        ) : undefined
      }
    >
      <HrFeatureGate>
        {selfId != null ? (
          <DocumentList subjectUserId={selfId} adminControls={false} />
        ) : (
          <Skeleton className="h-20 w-full" />
        )}
      </HrFeatureGate>
    </MeModuleShell>
  );
}
